/**
 * promoAdditionalDiscount.js
 * Pure helpers for promotion-driven additional discounts (loyalty gated).
 *
 * Business rules (spec: docs/superpowers/specs/2026-07-02-loyalty-promo-additional-discount-design.md):
 * - Additional discount applies ONLY when application_type is
 *   'straight_promotion_discount_with_additional_discount' and percent > 0.
 * - customer_eligibility 'all_customers'         -> always eligible.
 * - customer_eligibility 'loyalty_members_only'  -> customer must be a loyalty
 *   member, contact-verified (context.isVerified), AND their current tier id
 *   must be in eligible_tier_ids.
 * - Fail closed: missing tier id / malformed eligible_tier_ids -> not eligible.
 * - Base promo discount is never gated here; callers keep it either way.
 */

export const APPLICATION_TYPE_WITH_ADDITIONAL_DISCOUNT = 'straight_promotion_discount_with_additional_discount';

export const normalizeEligibleTierIds = (value) => {
    let raw = value;
    if (typeof raw === 'string' && raw.length > 0) {
        try {
            raw = JSON.parse(raw);
        } catch (error) {
            return [];
        }
    }
    if (!Array.isArray(raw)) {
        return [];
    }
    return raw.map((id) => parseInt(id)).filter((id) => !isNaN(id));
};

export const isPromoWithAdditionalDiscount = (itemPromo) => {
    return !!itemPromo && itemPromo.application_type === APPLICATION_TYPE_WITH_ADDITIONAL_DISCOUNT;
};

/**
 * Whether the customer qualifies for this promo AT ALL — gates the promo's base
 * discount, not just its additional layer. Applies to every promo regardless of
 * application_type: a 'loyalty_members_only' promo must never be selected (base
 * or additional) for a customer who doesn't qualify.
 * - customer_eligibility 'all_customers'        -> always eligible.
 * - customer_eligibility 'loyalty_members_only' -> customer must be a loyalty
 *   member, contact-verified (context.isVerified), AND their current tier id
 *   must be in eligible_tier_ids.
 * - Fail closed: missing tier id / malformed eligible_tier_ids -> not eligible.
 */
export const isPromoCustomerEligible = (itemPromo, context = {}) => {
    if (!itemPromo) {
        return false;
    }
    const eligibility = itemPromo.customer_eligibility || 'all_customers';
    if (eligibility === 'all_customers') {
        return true;
    }
    if (eligibility !== 'loyalty_members_only') {
        return false;
    }
    if (context.isLoyaltyMember !== true) {
        return false;
    }
    if (context.isVerified !== true) {
        return false;
    }
    const currentTierId = parseInt(context.currentTierId);
    if (isNaN(currentTierId)) {
        return false;
    }
    const eligibleTierIds = normalizeEligibleTierIds(itemPromo.eligible_tier_ids);
    return eligibleTierIds.includes(currentTierId);
};

export const resolvePromoAdditionalDiscount = (itemPromo, context = {}) => {
    const noDiscount = { apply: false, percent: 0 };
    if (!isPromoWithAdditionalDiscount(itemPromo)) {
        return noDiscount;
    }
    const percent = parseFloat(itemPromo.additional_discount_percent) || 0;
    if (percent <= 0) {
        return noDiscount;
    }
    return isPromoCustomerEligible(itemPromo, context) ? { apply: true, percent: percent } : noDiscount;
};

export const runPromoAdditionalDiscountTests = () => {
    const assert = (condition, message) => {
        if (!condition) {
            throw new Error(`FAIL: ${message}`);
        }
    };

    const basePromo = {
        id: 71330,
        discount: 40,
        additional_discount_percent: "10.00",
        customer_eligibility: "loyalty_members_only",
        application_type: "straight_promotion_discount_with_additional_discount",
        eligible_tier_ids: [5, 4, 3, 2, 1]
    };

    // normalizeEligibleTierIds
    assert(JSON.stringify(normalizeEligibleTierIds([5, 4, 3])) === '[5,4,3]', 'array passes through as ints');
    assert(JSON.stringify(normalizeEligibleTierIds("[5,4,3,2,1]")) === '[5,4,3,2,1]', 'JSON string parses');
    assert(JSON.stringify(normalizeEligibleTierIds(["5", "x", 3])) === '[5,3]', 'mixed values: keep parseable ints only');
    assert(JSON.stringify(normalizeEligibleTierIds("not-json")) === '[]', 'malformed string -> []');
    assert(JSON.stringify(normalizeEligibleTierIds(null)) === '[]', 'null -> []');
    assert(JSON.stringify(normalizeEligibleTierIds(undefined)) === '[]', 'undefined -> []');

    // isPromoWithAdditionalDiscount
    assert(isPromoWithAdditionalDiscount(basePromo) === true, 'new application type detected');
    assert(isPromoWithAdditionalDiscount({ application_type: 'straight_promotion_discount' }) === false, 'legacy application type -> false');
    assert(isPromoWithAdditionalDiscount(null) === false, 'null promo -> false');
    assert(isPromoWithAdditionalDiscount({}) === false, 'missing application_type -> false');

    // resolvePromoAdditionalDiscount - eligible, verified member
    const eligible = resolvePromoAdditionalDiscount(basePromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 });
    assert(eligible.apply === true && eligible.percent === 10, 'verified member with tier in list -> apply 10%');

    // member, tier NOT in list
    const wrongTier = resolvePromoAdditionalDiscount(basePromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 9 });
    assert(wrongTier.apply === false, 'member with tier not in list -> no additional');

    // non-member, loyalty_members_only
    const nonMember = resolvePromoAdditionalDiscount(basePromo, { isLoyaltyMember: false, isVerified: false, currentTierId: 3 });
    assert(nonMember.apply === false, 'non-member on loyalty_members_only -> no additional');

    // member, NOT verified -> no additional (mandatory verification gate)
    const unverifiedMember = resolvePromoAdditionalDiscount(basePromo, { isLoyaltyMember: true, isVerified: false, currentTierId: 3 });
    assert(unverifiedMember.apply === false, 'unverified member on loyalty_members_only -> no additional');

    // all_customers + new application type -> everyone, verification irrelevant
    const allCustomersPromo = { ...basePromo, customer_eligibility: 'all_customers' };
    const walkIn = resolvePromoAdditionalDiscount(allCustomersPromo, { isLoyaltyMember: false, isVerified: false, currentTierId: null });
    assert(walkIn.apply === true && walkIn.percent === 10, 'all_customers -> additional for walk-in too, no verification needed');

    // legacy application type -> never
    const legacyPromo = { ...basePromo, application_type: 'straight_promotion_discount' };
    const legacy = resolvePromoAdditionalDiscount(legacyPromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 });
    assert(legacy.apply === false, 'legacy promo -> no promo-driven additional');

    // eligible_tier_ids as JSON string
    const stringTiersPromo = { ...basePromo, eligible_tier_ids: "[5,4,3,2,1]" };
    const stringTiers = resolvePromoAdditionalDiscount(stringTiersPromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 5 });
    assert(stringTiers.apply === true, 'eligible_tier_ids JSON string works');

    // malformed eligible_tier_ids -> fail closed
    const badTiersPromo = { ...basePromo, eligible_tier_ids: "oops" };
    const badTiers = resolvePromoAdditionalDiscount(badTiersPromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 });
    assert(badTiers.apply === false, 'malformed eligible_tier_ids -> fail closed');

    // percent 0 / missing -> no additional
    const zeroPercent = resolvePromoAdditionalDiscount({ ...basePromo, additional_discount_percent: "0.00" }, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 });
    assert(zeroPercent.apply === false, 'percent 0 -> no additional');
    const noPercent = resolvePromoAdditionalDiscount({ ...basePromo, additional_discount_percent: null }, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 });
    assert(noPercent.apply === false, 'percent missing -> no additional');

    // missing tier id -> fail closed
    const noTier = resolvePromoAdditionalDiscount(basePromo, { isLoyaltyMember: true, isVerified: true, currentTierId: null });
    assert(noTier.apply === false, 'unresolvable tier id -> fail closed');

    // null promo / missing eligibility default
    assert(resolvePromoAdditionalDiscount(null, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 }).apply === false, 'null promo -> false');
    const noEligibility = resolvePromoAdditionalDiscount({ ...basePromo, customer_eligibility: null }, { isLoyaltyMember: false, isVerified: false, currentTierId: null });
    assert(noEligibility.apply === true, 'missing customer_eligibility defaults to all_customers (backend default)');

    // isPromoCustomerEligible — gates the BASE discount too, regardless of application_type.
    const plainLoyaltyPromo = {
        id: 88001,
        discount: 30,
        customer_eligibility: 'loyalty_members_only',
        application_type: 'straight_promotion_discount',
        eligible_tier_ids: [5, 4, 3, 2, 1]
    };
    assert(isPromoCustomerEligible(plainLoyaltyPromo, { isLoyaltyMember: false, isVerified: false, currentTierId: null }) === false, 'plain loyalty-only promo: non-member -> NOT eligible (base must not apply)');
    assert(isPromoCustomerEligible(plainLoyaltyPromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 }) === true, 'plain loyalty-only promo: verified member in eligible tier -> eligible');
    assert(isPromoCustomerEligible(plainLoyaltyPromo, { isLoyaltyMember: true, isVerified: false, currentTierId: 3 }) === false, 'plain loyalty-only promo: unverified member -> NOT eligible (mandatory verification)');
    assert(isPromoCustomerEligible(plainLoyaltyPromo, { isLoyaltyMember: true, isVerified: true, currentTierId: 9 }) === false, 'plain loyalty-only promo: member, wrong tier -> NOT eligible');
    assert(isPromoCustomerEligible({ ...plainLoyaltyPromo, customer_eligibility: 'all_customers' }, { isLoyaltyMember: false, isVerified: false, currentTierId: null }) === true, 'all_customers promo -> always eligible, no verification needed');
    assert(isPromoCustomerEligible(null, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 }) === false, 'null promo -> not eligible');
    assert(isPromoCustomerEligible({ ...plainLoyaltyPromo, customer_eligibility: null }, { isLoyaltyMember: false, isVerified: false, currentTierId: null }) === true, 'missing customer_eligibility defaults to all_customers');
    assert(isPromoCustomerEligible({ ...plainLoyaltyPromo, eligible_tier_ids: 'oops' }, { isLoyaltyMember: true, isVerified: true, currentTierId: 3 }) === false, 'malformed eligible_tier_ids -> fail closed');

    console.log('promoAdditionalDiscount: all tests passed');
};
